import { Suspense, useState } from 'react';

import { getCountry } from '@iwIntlProvider/CountryProvider';
import {
    IconExclamationTriangleOutline,
    IconInfoCircleOutline
} from '@iwoca/icons';
import { AlertBox, Modal } from '@iwoca/orion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import styles from './LinkedAccounts.module.css';
import { useGetTradingHistoryData } from '../useGetTradingHistoryData';
import SingleLinkedProvider from './singleLinkedProvider/SingleLinkedProvider';
import { UnlinkAccountModal } from './unlinkAccountModal/UnlinkAccountModal';
import { fetchDeleteOnlineAccount } from '../../../api/lendingAPI/LendingAPI';
import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import { TrueLayer } from '../../modals/linkOpenBankingModal/trueLayer';
import { SHOW_UPLOAD_DOCUMENTS_MODAL } from '../../modals/uploadDocumentsModal/UploadDocumentsModal.actions';
import MainContentPending from '../../pageStructure/mainContentPending';

const mapDispatchToProps = {
    uploadNewDocumentType: () => ({ type: SHOW_UPLOAD_DOCUMENTS_MODAL })
};

const LinkedAccounts = ({
    uploadNewDocumentType
}: {
    uploadNewDocumentType: () => { type: string };
}) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <div
                        className="col-span-2"
                        data-testid="linked-accounts-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <div className="col-span-2 row-span-2 max-sm:order-last max-sm:col-span-full">
                    <LinkedAccountsContent
                        uploadNewDocumentType={uploadNewDocumentType}
                    />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

const LinkedAccountsContent = ({
    uploadNewDocumentType
}: {
    uploadNewDocumentType: () => void;
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [unlinkingOnlineAccountId, setUnlinkingOnlineAccountId] = useState<
        string | null
    >(null);
    const [unlinkingLinkedAccountName, setUnlinkingLinkedAccountName] =
        useState<string | null>(null);
    const [unlinkingLinkedAccountStatus, setUnlinkingLinkedAccountStatus] =
        useState<'success' | 'error' | null>(null);

    const { linkedAccounts, hasRBL } = useGetTradingHistoryData();

    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const { mutate: confirmUnlink, isLoading: accountUnlinkingInProgress } =
        useMutation({
            mutationFn: () =>
                fetchDeleteOnlineAccount({
                    impersonatingStateKey: stateKey,
                    onlineAccountId: unlinkingOnlineAccountId
                }),
            onSuccess: async () => {
                setUnlinkingLinkedAccountStatus('success');
                await queryClient.invalidateQueries([
                    'online_accounts',
                    stateKey
                ]);
                resetUnlinkingState();
            },
            onError: () => {
                setUnlinkingLinkedAccountStatus('error');
            }
        });

    const openUnlinkModal = (id: string, name: string) => {
        setIsModalOpen(true);
        setUnlinkingOnlineAccountId(id);
        setUnlinkingLinkedAccountName(name);
        setUnlinkingLinkedAccountStatus(null);
    };

    const resetUnlinkingState = () => {
        setIsModalOpen(false);
        setUnlinkingOnlineAccountId(null);
        setUnlinkingLinkedAccountName(null);
        setUnlinkingLinkedAccountStatus(null);
    };

    return (
        <>
            <div className={styles.LinkedAccounts}>
                <h3 className={styles.title}>
                    <FormattedMessage
                        id="_.trading_data.linked_accounts.title"
                        defaultMessage="Linked Accounts"
                    />
                </h3>
                {hasRBL && !linkedAccounts.length && (
                    <AlertBox
                        className={styles.unlinked}
                        variant={getCountry() === 'DE' ? 'connect' : 'failure'}
                    >
                        {getCountry() === 'DE' ? (
                            <IconInfoCircleOutline />
                        ) : (
                            <IconExclamationTriangleOutline />
                        )}
                        <FormattedMessage
                            id="_.trading_data.linked_accounts.unlinked_error"
                            defaultMessage="Please relink your account so you don't fall behind"
                        />
                    </AlertBox>
                )}
                <div className={styles.accountList}>
                    {linkedAccounts.map((linkedAccount) => (
                        <SingleLinkedProvider
                            key={linkedAccount.providerId}
                            {...linkedAccount}
                            unlinkLinkedAccount={openUnlinkModal}
                        />
                    ))}
                </div>
            </div>
            <div className="mb-xl">
                <TrueLayer
                    heading={
                        <FormattedMessage
                            id="_.trading_data.linked_accounts.link_new_account"
                            defaultMessage="Link a new account"
                        />
                    }
                    handleUploadBankStatements={uploadNewDocumentType}
                />
            </div>
            <Modal
                className="max-w-[800px]"
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            >
                <UnlinkAccountModal
                    unlinkingLinkedAccountName={unlinkingLinkedAccountName}
                    accountUnlinkingInProgress={accountUnlinkingInProgress}
                    unlinkingLinkedAccountStatus={unlinkingLinkedAccountStatus}
                    onConfirm={confirmUnlink}
                    closeModal={resetUnlinkingState}
                />
            </Modal>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(LinkedAccounts);
